import { useFormikContext } from "formik";
import { Box, FormHelperText } from "@mui/material";
import { useTranslation } from "react-i18next";
import Switch from "./Switch";
import Enum from "./Enum";
import RangeInput from "./RangeInput";
import TextInput from "./TextInput";
import ArrayInput from "./Array";
import ObjectInput from "./Object";
import ScenarioInput from "./ScenarioInput";
import CustomViewsInput from "../CustomViews";
import AddressBookPhoneInput from "./AddressBookPhoneInput";
import UnsyncedIndicator from "./UnsyncedIndicator";
import VisibilityToggle from "./VisibilityToggle";
import {
  getCurrentFieldValue,
  isCustomViewsSetting,
  isFieldVisible,
} from "../utils";
import { resolveSettingIcon } from "../settingIcon";
import { useEffect } from "react";

export default function Renderer({
  id,
  name,
  value,
  onChange,
  groupName,
  schema,
  changedSettings,
  changeInfo,
  addressBook,
  // Only passed for top-level fields, so object/array properties are hidden
  // with their parent setting.
  visibility,
  // ARC-managed: read-only regardless of the schema.
  locked = false,
  ...restProps
}) {
  const { values, setFieldValue } = useFormikContext();
  const { t } = useTranslation();
  const schemaType = schema?.type?.toLowerCase();

  const isDisabled = () => {
    if (locked) return true;
    if (!schema?.hardValue) return false;

    if (schema.disabledValue !== undefined) return true;

    const { field: watchedFieldName, values: hardValueMap } = schema.hardValue;
    const watchedFieldValue = getCurrentFieldValue(
      values,
      id,
      watchedFieldName
    );

    // Disabled if there's a hardValue mapping for current watched field value
    return hardValueMap && watchedFieldValue in hardValueMap;
  };

  useEffect(() => {
    if (!schema?.hardValue || locked) return;

    const { field: watchedFieldName, values: hardValueMap } = schema.hardValue;

    // Get the current value of the watched field
    const watchedFieldValue = getCurrentFieldValue(
      values,
      id,
      watchedFieldName
    );

    // Check if there's a hardValue for this watched field value
    if (hardValueMap && watchedFieldValue in hardValueMap) {
      const newValue = hardValueMap[watchedFieldValue];

      // Only update if the current value is different
      if (value !== newValue) {
        setFieldValue(id, newValue);
        onChange?.(id, newValue);
      }
    }
  }, [schema?.hardValue, locked, values, id, value, setFieldValue, onChange]);

  if (!isFieldVisible(schema, id, values)) {
    return null;
  }

  // <Group> already filters these out; this covers direct callers.
  const settingHidden = !!visibility?.isSettingHidden?.(id);
  if (visibility?.enforced && settingHidden) {
    return null;
  }
  const visibilityToggle = visibility?.editable ? (
    <VisibilityToggle
      hidden={settingHidden}
      onToggle={() => visibility.toggleSetting(id)}
      sx={{ mr: 0.5 }}
    />
  ) : null;
  const hiddenSx = { opacity: settingHidden && visibility?.editable ? 0.55 : 1 };

  const _props = {
    name,
    value,
    onChange,
    id,
    groupName,
    schema,
    disabled: isDisabled(),
    locked,
    ...restProps,
  };

  const renderInput = () => {
    switch (schemaType) {
      case "boolean":
        return <Switch {..._props} />;
      case "enum":
        return <Enum {..._props} />;
      case "number":
      case "integer":
        return <RangeInput {..._props} />;
      case "string": {
        // Backend inputHelper wins; else infer from the well-known field name.
        const helper =
          schema?.inputHelper ||
          (name === "scenario"
            ? "uuid"
            : name === "phoneNumber"
            ? "phoneNumber"
            : null);
        // A uuid field is a scenario reference — render a scenario picker.
        if (helper === "uuid") return <ScenarioInput {..._props} />;
        // callFunction phone: also pick a device/contact from the address book.
        if (helper === "phoneNumber" && addressBook)
          return <AddressBookPhoneInput {..._props} />;
        const stringProps =
          helper && helper !== schema?.inputHelper
            ? { ..._props, schema: { ...schema, inputHelper: helper } }
            : _props;
        return <TextInput {...stringProps} />;
      }
      case "array":
        // Custom views have a bespoke editor (phone mockup + tile composer).
        if (isCustomViewsSetting(schema, groupName, name))
          return <CustomViewsInput {..._props} />;
        return <ArrayInput {..._props} />;
      case "object":
        return <ObjectInput {..._props} />;
      default:
        return (
          <div className="text-red-500">
            Unknown schema type: {schema?.type}
          </div>
        );
    }
  };

  const isChanged = !!changedSettings?.has(id);
  const info = changeInfo?.get?.(id);
  const FieldIcon = resolveSettingIcon(schema?.icon);
  const isContainer = schemaType === "array" || schemaType === "object";
  const showGutter = !isContainer && (!!FieldIcon || isChanged);
  const notice = schema?.notice;
  const noticeNode = notice ? (
    <Box sx={{ pl: showGutter ? "32px" : 2, pr: 2, pb: 0.75 }}>
      <FormHelperText sx={{ m: 0 }}>
        {t(notice, { defaultValue: notice })}
      </FormHelperText>
    </Box>
  ) : null;

  // Arrays/objects are container blocks with their own title (which carries the
  // icon) — mark unsynced in the corner rather than a leading gutter.
  if (isContainer) {
    return (
      <Box sx={{ position: "relative" }}>
        {isChanged && (
          <UnsyncedIndicator
            info={info}
            schema={schema}
            sx={{
              position: "absolute",
              // Keep clear of the eye toggle in the same corner.
              right: visibilityToggle ? 44 : 10,
              top: 10,
              zIndex: 1,
            }}
          />
        )}
        {visibilityToggle && (
          <Box sx={{ position: "absolute", right: 4, top: 4, zIndex: 2 }}>
            {visibilityToggle}
          </Box>
        )}
        {visibilityToggle ? (
          <Box sx={hiddenSx}>{renderInput()}</Box>
        ) : (
          renderInput()
        )}
        {noticeNode}
      </Box>
    );
  }

  // Simple fields: leading gutter shows the icon, or the unsynced marker.
  // No icon and unchanged → render flush.
  if (!showGutter) {
    return (
      <Box>
        {visibilityToggle ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ flex: 1, minWidth: 0, ...hiddenSx }}>
              {renderInput()}
            </Box>
            {visibilityToggle}
          </Box>
        ) : (
          renderInput()
        )}
        {noticeNode}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "stretch" }}>
        <Box
          sx={{
            width: 24,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            pl: 1,
          }}
        >
          {isChanged ? (
            <UnsyncedIndicator info={info} schema={schema} />
          ) : (
            <FieldIcon color="action" size={0.85} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, ...hiddenSx }}>{renderInput()}</Box>
        {visibilityToggle}
      </Box>
      {noticeNode}
    </Box>
  );
}
